/**
 * What the TrueType glyph loader (`sfntGlyf.ts`) asks of a hinter, and the glyph zone it hands it:
 * FreeType's bytecode interpreter (`truetype/`), which TextMesh loads on demand for a font that
 * FreeType hints natively. A font with neither a font program nor a control value program of more
 * than 7 bytes goes to FreeType's auto-hinter instead (`ftobjs.c`, `FT_Load_Glyph`), whatever its
 * glyph programs hold, unless FreeType lists it as tricky (`trickyFont.ts`).
 *
 * Portions ported from FreeType (FreeType Project License).
 * Copyright (C) 1996-2026 by David Turner, Robert Wilhelm, and Werner Lemberg.
 * See THIRD-PARTY-NOTICES.md.
 */
import { isTrickyFont } from './trickyFont';
/** `FT_CURVE_TAG_TOUCH_X`: an instruction has moved the point along x. */
export const TAG_TOUCH_X = 0x08;
/** `FT_CURVE_TAG_TOUCH_Y`: an instruction has moved the point along y. */
export const TAG_TOUCH_Y = 0x10;
export const TAG_TOUCH_BOTH = TAG_TOUCH_X | TAG_TOUCH_Y;
/** The four phantom points that follow a glyph's own: left and right side bearing, top and bottom. */
export const PHANTOM_POINT_COUNT = 4;
/** `tt_glyphzone_new`: a zone of `pointCount` points at the origin, untouched, with `contourEnds`. */
export function newGlyphZone(pointCount, contourEnds, firstPoint) {
    return {
        pointCount,
        contourCount: contourEnds.length,
        orgX: new Float64Array(pointCount),
        orgY: new Float64Array(pointCount),
        curX: new Float64Array(pointCount),
        curY: new Float64Array(pointCount),
        orusX: new Float64Array(pointCount),
        orusY: new Float64Array(pointCount),
        tags: new Uint8Array(pointCount),
        contourEnds,
        firstPoint,
    };
}
/** The size of a control value program that the auto-hinter still takes over from (`ftobjs.c`). */
const LARGEST_AUTOHINTED_CONTROL_VALUE_PROGRAM = 7;
/** Whether FreeType runs the font's own TrueType hints rather than its auto-hinter. */
export function isNativelyHinted(sfnt) {
    const fontProgramLength = sfnt.tables.get('fpgm')?.length ?? 0;
    const controlValueProgramLength = sfnt.tables.get('prep')?.length ?? 0;
    return (fontProgramLength > 0 ||
        controlValueProgramLength > LARGEST_AUTOHINTED_CONTROL_VALUE_PROGRAM ||
        isTrickyFont(sfnt));
}
//# sourceMappingURL=glyphHinting.js.map